import { ContractTypeEnum } from '../../../../resources/base/models';
import { CountryCodesEnum } from '../../../../resources/base/models/country';
import { BooleanEnum } from '../../../../resources/base/models/enums';
import { DataStorageCustomDataType } from '../../../../services/redux/features/dataStorage.redux';
import { formatCityNameLettersAndSpaceOnly } from '../../utils/formatCityString';
import { apiDateToUiDate } from '../../utils/formatDate';
import { getOccupationGroupFlags } from '../occupationBasedFieldsFilter';
import {
  employmentsToInvolvementInCompanies,
  getMainEmployment,
  getPersonalExpenseOfType,
  getPersonalIncomeOfType,
  InvolvementInCompanies,
} from '../utils';

export const getContactDataFormInitialValues = (
  customerData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse
) => {
  const _ = customerData;

  type EmploymentCategory =
    | undefined
    | Extract<CreditIntegration.Schemas.InvolvedInCompanyCategoryType, 'MAIN_EMPLOYMENT'>;

  return {
    gender: _.gender,
    firstName: _.firstName,
    lastName: _.lastName,
    birthName: _.birthName,
    dateOfBirth: apiDateToUiDate(_.dateOfBirth),
    profession: getMainEmployment(_)?.posFrOccupation,
    _employmentCategory: getMainEmployment(_)?.milesInvolvedInCompanyCategory || (undefined as EmploymentCategory),
    email: _.contactData?.emails?.[0].emailAddress?.toLowerCase(),
    mobilePhoneCountryCode: _.contactData?.mobilePhoneCountryCode,
    mobilePhone: _.contactData?.mobilePhoneNumber,
    telephonePhone: _.contactData?.telephoneNumber && `+33 ${_.contactData?.telephoneNumber}`,
    birthCountry: _.countryOfBirth ?? CountryCodesEnum.FR,
    zipCodeOfBirth: _.placeOfBirthZipCode,
    placeOfBirth: formatCityNameLettersAndSpaceOnly(_.placeOfBirth),
    nationality: _.nationality ?? CountryCodesEnum.FR,
    residenceStatus: _.financialDisclosure?.housingSituation,
  };
};

export type ContactDataFormType = Partial<ReturnType<typeof getContactDataFormInitialValues>>;

export const getAddressDataFormInitialValues = (
  customerData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse
) => {
  const _ = customerData;

  return {
    addressLine1: _.addresses?.[0].addressLine1,
    optionalLine: _.addresses?.[0].optionalLine,
    zipCode: _.addresses?.[0].zipCode,
    city: _.addresses?.[0].city,
    countryCode: _.addresses?.[0].countryCode ?? CountryCodesEnum.FR,
  };
};

export type AddressDataFormType = ReturnType<typeof getAddressDataFormInitialValues>;

export const getFinancialDataFormInitialValues = (
  mainPosFrOccupation: CreditIntegration.Schemas.OccupationEnumValue,
  customerData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse
) => {
  const _ = customerData;

  const { isOccupationGroupA } = getOccupationGroupFlags(mainPosFrOccupation);

  return {
    // applicationFor: /* TODO: missing in BFT data model */ LVDLDropdownEnum.Beneficiary as any /* placeholder */,
    maritalStatus: _.maritalStatus,
    numberOfDependentChildren: _.numberOfDependentChildren ?? undefined,
    contractType: isOccupationGroupA ? ContractTypeEnum.NO_CONTRACT : _.employments?.[0].contractType,
    employmentStartDate: apiDateToUiDate(_.employments?.[0].period?.startDate),
    employer: _.employments?.[0].employer?.name,
    countryCodeEmployer: _.employments?.[0].employer?.addresses?.[0].countryCode ?? CountryCodesEnum.FR,
    zipCodeEmployer: _.employments?.[0].employer?.addresses?.[0].zipCode,
    cityEmployer: _.employments?.[0].employer?.addresses?.[0].city,
    netIncomeAmount: getPersonalIncomeOfType(_, 'PERSONAL_INCOME'),
    additionalIncomeAmount: getPersonalIncomeOfType(_, 'ADDITIONAL'),
    monthlyRent: getPersonalExpenseOfType(_, 'RENT'),
    otherMonthlyCosts: getPersonalExpenseOfType(_, 'ADDITIONAL'),
  };
};

export type FinancialDataFormType = Partial<ReturnType<typeof getFinancialDataFormInitialValues>>;

export const getPaymentDataFormInitialValues = (
  customerData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse,
  dataStorageData: DataStorageCustomDataType
) => {
  const _ = customerData;

  return {
    bankAccountOwner: dataStorageData.bankAccountOwner,
    openingDate: dataStorageData.openingDate,
    iban: dataStorageData.iban,
    bic: dataStorageData.bic,
  };
};

export type PaymentDataFormType = ReturnType<typeof getPaymentDataFormInitialValues>;

export const getCompanyDataFormInitialValues = (
  creditApplicationData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse,
  isSectionSubmitted: boolean = false
) => {
  const involvementInCompanies = employmentsToInvolvementInCompanies(creditApplicationData);
  const isInvolvedInCompanies = Object.keys(involvementInCompanies).length > 0;

  let oneCustomerHasCompany: BooleanEnum | undefined;
  if (isInvolvedInCompanies) {
    oneCustomerHasCompany = BooleanEnum.true;
  } else if (isSectionSubmitted) {
    oneCustomerHasCompany = BooleanEnum.false;
  }

  return {
    oneCustomerHasCompany,
    ...involvementInCompanies,
  } as InvolvementInCompanies;
};

export type CompanyDataFormType = ReturnType<typeof getCompanyDataFormInitialValues>;
